import React from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  handleUpdateFields,
  handleClearActivitaionField,
} from "store/liveOps/bonuses/slice";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";

interface Props {
  currency: string;
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
}
const BonusesSettingsActivationRulesTab: React.FC<Props> = ({
  currency,
  fieldErrors,
  clearFieldError,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const payload = useAppSelector((store) => store.bonuses.payload);
  const currentItem = payload?.additionalConfigs.filter(
    (el) => el?.currency === currency,
  )[0];

  const handleDataActivation = (key: string, value: string, type: string) => {
    if (currentItem) {
      const currentActivationItem = currentItem.activations.filter(
        (item) => item.type === type,
      )[0];
      if (type === "deposit" && !currentActivationItem) {
        // @ts-ignore
        dispatch(
          handleUpdateFields({
            ...payload,
            additionalConfigs: [
              ...payload?.additionalConfigs.filter(
                (el) => el?.currency !== currency,
              ),
              {
                currency: currency,
                rewards: currentItem.rewards,
                betsRangeMax: currentItem.betsRangeMax,
                betsRangeMin: currentItem.betsRangeMin,
                activations: [
                  ...currentItem.activations.filter(
                    (item) => item.type !== "noDeposit",
                  ),
                  {
                    type: "deposit",
                    currency: currency,
                  },
                ],
              },
            ],
          }),
        );
      } else if (type === "noDeposit" && !currentActivationItem) {
        // @ts-ignore
        dispatch(
          handleUpdateFields({
            ...payload,
            additionalConfigs: [
              ...payload?.additionalConfigs.filter(
                (el) => el?.currency !== currency,
              ),
              {
                currency: currency,
                rewards: currentItem.rewards,
                betsRangeMax: currentItem.betsRangeMax,
                betsRangeMin: currentItem.betsRangeMin,
                activations: [
                  ...currentItem.activations.filter(
                    (item) => item.type !== "deposit",
                  ),
                  {
                    currency: currency,
                    type: "noDeposit",
                  },
                ],
              },
            ],
          }),
        );
      } else {
        // @ts-ignore
        dispatch(
          handleUpdateFields({
            ...payload,
            additionalConfigs: [
              ...payload?.additionalConfigs.filter(
                (el) => el?.currency !== currency,
              ),
              {
                currency: currency,
                rewards: currentItem.rewards,
                betsRangeMax: currentItem.betsRangeMax,
                betsRangeMin: currentItem.betsRangeMin,
                activations: currentActivationItem
                  ? [
                      ...currentItem.activations.filter(
                        (item) => item.type !== type,
                      ),
                      {
                        ...currentItem.activations.filter(
                          (item) => item.type === type,
                        )[0],
                        [key]: value,
                      },
                    ]
                  : [
                      ...currentItem.activations,
                      {
                        [key]: value,
                        type: type,
                        currency: currency,
                      },
                    ],
              },
            ],
          }),
        );
      }
    } else {
      dispatch(
        // @ts-ignore
        handleUpdateFields({
          ...payload,
          additionalConfigs: [
            // @ts-ignore
            ...payload?.additionalConfigs,
            {
              currency: currency,
              rewards: [],
              betsRangeMax: 0,
              betsRangeMin: 0,
              activations: [
                {
                  [key]: value,
                  type: type,
                  currency: currency,
                },
              ],
            },
          ],
        }),
      );
    }
  };

  return (
    <div className="bonus-inner-form small">
      <div className="item">
        <SwitchBtn
          activeTxt={t("deposit")}
          isActive={
            !!currentItem?.activations.filter((el) => el.type === "deposit")[0]
          }
          handleClick={() => {
            const isActive = !!currentItem?.activations.find(
              (el) => el.type === "deposit",
            );
            if (isActive) {
              clearFieldError("depositMin");
              clearFieldError("depositMax");
              clearFieldError("depositNumber");
            }
            handleDataActivation(
              "type",
              isActive ? "noDeposit" : "deposit",
              isActive ? "noDeposit" : "deposit",
            );
          }}
        />
        {!!currentItem?.activations.filter((el) => el.type === "deposit")[0] ? (
          <div className="row row-3">
            <InnerInput
              value={
                currentItem?.activations.filter(
                  (el) => el.type === "deposit",
                )[0].numberOfDeposit ?? ""
              }
              placeholder={t("depositNumber")}
              label={t("depositNumber")}
              tooltipText={t("sequentialNumberOfTheAccountDeposit")}
              tooltipPosition="top"
              type="number"
              id="acivation-numberOfDeposit"
              onChange={(e) => {
                handleDataActivation("numberOfDeposit", e, "deposit");
                clearFieldError("depositNumber");
              }}
              hasError={Boolean(fieldErrors?.["depositNumber"])}
            />
            <InnerInput
              value={
                currentItem?.activations.filter(
                  (el) => el.type === "deposit",
                )[0].min ?? ""
              }
              placeholder={t("minDeposit")}
              label={t("minDeposit")}
              tooltipText={t("depositAmountRange")}
              tooltipPosition="top"
              type="number"
              id="acivation-min"
              onChange={(e) => {
                handleDataActivation("min", e, "deposit");
                clearFieldError("depositMin");
              }}
              hasError={Boolean(fieldErrors?.["depositMin"])}
            />
            <InnerInput
              value={
                currentItem?.activations.filter(
                  (el) => el.type === "deposit",
                )[0].max ?? ""
              }
              placeholder={t("maxDeposit")}
              label={t("maxDeposit")}
              tooltipText={t("depositAmountRange")}
              tooltipPosition="top"
              type="number"
              id="acivation-max"
              onChange={(e) => {
                handleDataActivation("max", e, "deposit");
                clearFieldError("depositMax");
              }}
              hasError={Boolean(fieldErrors?.["depositMax"])}
            />
          </div>
        ) : (
          <InnerInput
            value={""}
            placeholder={t("noDepositRequired")}
            label={t("noDepositRequired")}
            type="string"
            id="acivation-dis"
            disabled
            onChange={(e) => handleDataActivation("payoutX", e, "deposit")}
          />
        )}
      </div>
      <div className="item">
        <SwitchBtn
          activeTxt={t("cancellationAmount")}
          isActive={
            !!currentItem?.activations.filter(
              (el) => el.type === "cancellationAmount",
            )[0]
          }
          handleClick={() => {
            const isCancellationActive = !!currentItem?.activations.find(
              (el) => el.type === "cancellationAmount",
            );

            if (isCancellationActive) {
              dispatch(
                handleClearActivitaionField({
                  type: "cancellationAmount",
                  currency: currency,
                }),
              );

              clearFieldError("cancellationAmount");
            } else {
              handleDataActivation(
                "type",
                "cancellationAmount",
                "cancellationAmount",
              );
            }
          }}
        />
        <div className="row">
          <div className="field field--canceledWithdrawal">
            <InnerInput
              value={
                currentItem?.activations.filter(
                  (el) => el.type === "cancellationAmount",
                )[0]?.min ?? ""
              }
              disabled={
                currentItem?.activations.filter(
                  (el) => el.type === "cancellationAmount",
                )[0]
                  ? false
                  : true
              }
              type="number"
              id="acivation-cancellationAmount"
              placeholder={t("canceledWithdrawal")}
              label={t("canceledWithdrawal")}
              tooltipText={t("canceledWithdrawalAmount")}
              tooltipPosition="top"
              onChange={(e) => {
                handleDataActivation("min", e, "cancellationAmount");
                clearFieldError("cancellationAmount");
              }}
              hasError={Boolean(fieldErrors?.["cancellationAmount"])}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BonusesSettingsActivationRulesTab;
